/**
 * WalletHistoryPage — the coin ledger: what was charged, granted or lost, and when.
 *
 * Data: GET /billing/wallet/history (newest first) + GET /billing/wallet for the
 * current balance.
 *
 * Auth: requires login (protected by RequireAuth in App.tsx)
 */

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { getWalletHistory, CoinTransactionOut } from "@/services/api"
import { useWalletStore } from "@/stores/walletStore"

const PAGE_SIZE = 50

const TYPE_LABEL_KEY: Record<CoinTransactionOut["type"], string> = {
  grant: "billing.coinType.grant",
  daily_refill: "billing.coinType.dailyRefill",
  game_start: "billing.coinType.gameStart",
  forfeit: "billing.coinType.forfeit",
  chargeback: "billing.coinType.chargeback",
}

function formatMoment(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

export function WalletHistoryPage() {
  const { t, i18n } = useTranslation()
  const balance = useWalletStore((s) => s.balance)
  const refresh = useWalletStore((s) => s.refresh)
  const [entries, setEntries] = useState<CoinTransactionOut[] | null>(null)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    let cancelled = false
    getWalletHistory(PAGE_SIZE, 0)
      .then((page) => {
        if (cancelled) return
        setEntries(page)
        setOffset(page.length)
        setHasMore(page.length === PAGE_SIZE)
      })
      .catch(() => {
        if (cancelled) return
        setEntries([])
        toast.error(t("billing.coinHistoryError"))
      })
    return () => {
      cancelled = true
    }
    // Runs once: `t` only supplies toast copy and must not retrigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = async () => {
    setIsLoadingMore(true)
    try {
      const page = await getWalletHistory(PAGE_SIZE, offset)
      setEntries((current) => [...(current ?? []), ...page])
      setOffset(offset + page.length)
      setHasMore(page.length === PAGE_SIZE)
    } catch {
      toast.error(t("billing.coinHistoryError"))
    } finally {
      setIsLoadingMore(false)
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-foreground font-display font-bold text-xl">
            {t("billing.coinHistoryTitle")}
          </h1>
          <Button variant="ghost" asChild className="text-muted-foreground hover:text-foreground">
            <Link to="/profile">{t("profile.back")}</Link>
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground text-lg">
              {t("billing.coinBalance")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-black">
              💎 {balance ?? "—"}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            {entries === null ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-12 bg-muted rounded" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6">
                {t("billing.coinHistoryEmpty")}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {entries.map((entry, index) => (
                  <li
                    key={`${entry.created_at}-${index}`}
                    className="py-3 flex items-start justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-foreground">
                        {t(TYPE_LABEL_KEY[entry.type])}
                        {entry.mode_type !== null && (
                          <span className="text-muted-foreground"> · {entry.mode_type}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatMoment(entry.created_at, i18n.language)}
                        {entry.player_count !== null && (
                          <> · {t("billing.coinHistoryPlayers", { count: entry.player_count })}</>
                        )}
                      </p>
                    </div>
                    <span
                      className={
                        entry.amount < 0
                          ? "text-destructive font-bold whitespace-nowrap"
                          : "text-primary font-bold whitespace-nowrap"
                      }
                    >
                      {entry.amount > 0 ? `+${entry.amount}` : entry.amount} 💎
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {hasMore && (
              <Button
                variant="outline"
                className="w-full mt-4"
                disabled={isLoadingMore}
                onClick={loadMore}
              >
                {isLoadingMore ? t("common.loading") : t("billing.coinHistoryMore")}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
